import { storeToRefs } from "pinia";

import { useAuthStore } from "@/stores/Auth";
import { useTradeAlertsStore } from "@/stores/TradeAlerts";

import { useDateRangeFilter } from "@/composables/filters/date-range-filter";
import { useProductFilter } from "@/composables/filters/product-filter";
import { useTextFilter } from "@/composables/filters/text-filter";
import { useContentFilter } from "@/composables/filters/use-content-filter";

import { SearchableTicker, TradeAlert } from "@tmi/types/entities";

/*********************************************************************/
//                 GLOBALLY SHARED REACTIVE VARIABLES                //
/*********************************************************************/
export function useTradeAlertFilters() {
    const { isAuthenticated } = storeToRefs( useAuthStore() );

    const tradeAlertsStore = useTradeAlertsStore();
    const { allTradeAlerts } = storeToRefs( tradeAlertsStore );

    // ------------------------------------ FILTERS ------------------------------------
    // The time interval that captures the date range for which to return trade alerts
    const { isValidDateWithinDateRange } = useDateRangeFilter();

    // The products that have been selected to limit the results (inclusive)
    const { filterOutItemsThatDoNotContainSelectedProducts } = useProductFilter();

    // The search text entered by the user
    const { filterOutItemsThatDoNotContainSearchText } = useTextFilter();

    const {
        filtered: filteredTradeAlerts,
        filteredPage: filteredTradeAlertsPage,
        hasAnyResults: hasAnyTradeAlertResults,
        searchResultsLabel: tradeAlertsSearchResultsLabel,
        loading,
    } = useContentFilter< TradeAlert >( {
        itemNoun: 'trade alert',
        exposeLoading: true,
        computeFiltered: () => {
            // Historical behavior: only shows results for authenticated users.
            if ( !isAuthenticated.value ) return [];

            return allTradeAlerts.value
                .filter( ( tradeAlert: TradeAlert ) => filterOutItemsThatDoNotContainSelectedProducts( tradeAlert.products ) )
                .filter( ( tradeAlert: TradeAlert ) => isValidDateWithinDateRange( tradeAlert.publishedDate ) )
                .filter( ( tradeAlert: TradeAlert ) => filterOutItemsThatDoNotContainSearchText( [
                    ...tradeAlert.tickers.map( ( ticker: SearchableTicker ) => ticker.name ),
                    tradeAlert.text,
                    tradeAlert.body2,
                    tradeAlert.title,
                ] ) );
        },
    } );

    return {
        // T/F based on if filteredTradeAlerts has any items
        hasAnyTradeAlertResults,
        // '#### results found'
        tradeAlertsSearchResultsLabel,

        // The current single page sub-collection
        filteredTradeAlertsPage,
        // All trade alerts that match the current filter
        filteredTradeAlerts,

        // State variable to track if the filter is currently processing anything
        loading
    }
}


export type TradeAlertFilters = ReturnType< typeof useTradeAlertFilters >;
